import { useState } from 'react'
import { Link } from '@inertiajs/react'
import { Badge, Button, Heading, Text } from '@radix-ui/themes'
import { Heart } from 'lucide-react'
import type { EventFeedEvent } from '@/types/shared'
import { useLikes } from '@/shared/likes/useLikes'
import { useBrand } from '@/shared/brand/useBrand'
import EventPoster from '@/shared/events/EventPoster'

interface Props {
  /** Soonest first. The first one is the headliner. */
  upcoming: EventFeedEvent[]
  /** Newest first. */
  recent: EventFeedEvent[]
  /** Over the headliner: "Next event", "Next near you". */
  headlinerLabel?: string
  /** Off on a track's own page, where the track goes without saying. */
  showTrack?: boolean
  /** Recent events shown before "All past events". */
  recentLimit?: number
  emptyMessage?: string
}

/**
 * Events as a blog: the next one as a headliner, the rest of the upcoming ones
 * as a dated list, then recent ones as poster cards. The events index and the
 * track page both use it, and it's what a branded site lands on.
 */
export default function EventFeed({
  upcoming,
  recent,
  headlinerLabel = 'Next event',
  showTrack = true,
  recentLimit = 3,
  emptyMessage = 'No events yet.',
}: Props) {
  const { likeIdFor, toggleLike } = useLikes('Event')
  const [showAllRecent, setShowAllRecent] = useState(false)

  const [headliner, ...comingUp] = upcoming
  const shownRecent = showAllRecent ? recent : recent.slice(0, recentLimit)

  if (upcoming.length === 0 && recent.length === 0) {
    return <Text as="p" color="gray">{emptyMessage}</Text>
  }

  return (
    <div className="flex flex-col gap-12">
      {headliner ? (
        <Headliner
          event={headliner}
          label={headlinerLabel}
          showTrack={showTrack}
          liked={likeIdFor(headliner) != null}
          onToggle={() => toggleLike(headliner)}
        />
      ) : (
        <Text as="p" color="gray">No upcoming events yet.</Text>
      )}

      {comingUp.length > 0 && (
        <section className="flex flex-col gap-4">
          <Heading as="h2" size="6">Coming up</Heading>
          <EventRows events={comingUp} showTrack={showTrack} />
        </section>
      )}

      {recent.length > 0 && (
        <section className="flex flex-col gap-4">
          <Heading as="h2" size="6">Recent events</Heading>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {shownRecent.map((event) => (
              <RecentCard key={event.id} event={event} showTrack={showTrack} />
            ))}
          </div>
          {recent.length > recentLimit && (
            <div>
              <Button variant="soft" color="gray" onClick={() => setShowAllRecent(!showAllRecent)}>
                {showAllRecent ? 'Show fewer' : `All past events (${recent.length})`}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  )
}

interface EntryProps {
  event: EventFeedEvent
  showTrack: boolean
}

interface LikeableEntryProps extends EntryProps {
  liked: boolean
  onToggle: () => void
}

function Headliner({ event, label, showTrack, liked, onToggle }: LikeableEntryProps & { label: string }) {
  return (
    <section
      aria-labelledby={`headliner-${event.id}`}
      className="flex flex-col overflow-hidden rounded-xl border border-gray5 bg-panel sm:flex-row"
    >
      <div className="h-64 sm:h-auto sm:min-h-80 sm:w-1/2">
        <EventPoster name={event.name} date={event.date} url={event.poster_url} fit="contain" size="lg" />
      </div>
      <div className="flex flex-col gap-3 p-6 sm:w-1/2 sm:p-8">
        <Text size="2" weight="bold" className="uppercase tracking-wider text-accent11">
          {label} · {whenLabel(event)}
        </Text>
        <Heading as="h2" size="8" id={`headliner-${event.id}`}>
          <Link href={`/events/${event.id}`} className="hover:underline">{event.name}</Link>
        </Heading>
        <Text as="p" size="3" color="gray">
          {event.date}
          {event.start_time && ` · ${event.start_time}`}
        </Text>
        <HostLine event={event} showTrack={showTrack} />
        {event.excerpt && <Text as="p" size="3">{event.excerpt}</Text>}
        <div className="flex flex-wrap gap-2">
          <ApplicationsBadge event={event} />
          <Badge variant="soft" color="gray">Spectators welcome</Badge>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {event.applications_status === 'open' && (
            <Button asChild size="3">
              <Link href={`/events/${event.id}#apply`}>Apply</Link>
            </Button>
          )}
          <Button asChild size="3" variant="outline">
            <Link href={`/events/${event.id}`}>Event details</Link>
          </Button>
          <LikeToggle liked={liked} onToggle={onToggle} />
        </div>
      </div>
    </section>
  )
}

/** Upcoming events as dated rows — the feed's "Coming up", and the events
 *  index's "Your events" above the feed. */
export function EventRows({ events, showTrack = true }: { events: EventFeedEvent[]; showTrack?: boolean }) {
  const { likeIdFor, toggleLike } = useLikes('Event')

  return (
    <ul className="overflow-hidden rounded-xl border border-gray5 bg-panel">
      {events.map((event) => (
        <EventRow
          key={event.id}
          event={event}
          showTrack={showTrack}
          liked={likeIdFor(event) != null}
          onToggle={() => toggleLike(event)}
        />
      ))}
    </ul>
  )
}

function EventRow({ event, showTrack, liked, onToggle }: LikeableEntryProps) {
  return (
    <li className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-gray4 px-5 py-4 last:border-b-0">
      <div className="w-14 flex-none text-center" aria-hidden="true">
        <div className="text-xs font-bold text-accent11">{event.month}</div>
        <div className="text-2xl font-bold">{event.day}</div>
      </div>
      <div className="flex min-w-0 flex-[1_1_16rem] flex-col gap-1">
        <Link href={`/events/${event.id}`} className="truncate text-[17px] font-semibold hover:underline">
          {event.name}
        </Link>
        <Text size="2" color="gray">
          <span className="sr-only">{event.date} · </span>
          {whenLabel(event)}
        </Text>
        <HostLine event={event} showTrack={showTrack} size="2" />
      </div>
      <ApplicationsBadge event={event} />
      <div className="flex items-center gap-3">
        <LikeToggle liked={liked} onToggle={onToggle} />
        <Link href={`/events/${event.id}`} className="text-sm font-semibold text-accent11 hover:underline">
          Details
        </Link>
      </div>
    </li>
  )
}

function RecentCard({ event, showTrack }: EntryProps) {
  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-gray5 bg-panel">
      <Link href={`/events/${event.id}`} tabIndex={-1} aria-hidden="true" className="h-36">
        <EventPoster name={event.name} date={event.date} url={event.poster_card_url} />
      </Link>
      <div className="flex flex-col gap-1 px-4 py-3">
        <Link href={`/events/${event.id}`} className="font-semibold hover:underline">{event.name}</Link>
        <Text size="2" color="gray">{event.date} · {whenLabel(event)}</Text>
        <HostLine event={event} showTrack={showTrack} size="2" />
      </div>
    </article>
  )
}

/** "Hosted by [club] at [track]", or just the track. On a track's own page,
 *  only the club — and nothing at all for the track's own events. Plain text
 *  on a branded site, which has no track or club pages to link to. */
function HostLine({ event, showTrack, size = '3' }: EntryProps & { size?: '2' | '3' }) {
  const brand = useBrand()
  const club = event.club_name && event.club_slug && (
    brand ? event.club_name : (
      <Link href={`/clubs/${event.club_slug}`} className="hover:underline">{event.club_name}</Link>
    )
  )
  const track = brand ? event.track_name : (
    <Link href={`/tracks/${event.track_slug}`} className="hover:underline">{event.track_name}</Link>
  )

  if (!showTrack && !club) return null

  return (
    <Text as="p" size={size} color="gray">
      {club ? <>Hosted by {club}{showTrack && <> at {track}</>}</> : track}
    </Text>
  )
}

/** Where applications stand. Nothing for past events, or ones that take none. */
export function ApplicationsBadge({ event }: { event: EventFeedEvent }) {
  if (event.is_past) return null

  switch (event.applications_status) {
    case 'open':
      return <Badge variant="soft" color="green">Applications open</Badge>
    case 'not_open_yet':
      return <Badge variant="soft" color="gray">Applications open {event.applications_open_at_display}</Badge>
    case 'closed':
      return <Badge variant="soft" color="gray">Applications closed</Badge>
    default:
      return null
  }
}

function LikeToggle({ liked, onToggle }: { liked: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-label={liked ? 'Remove from watchlist' : 'Add to watchlist'}
      aria-pressed={liked}
      onClick={onToggle}
      className={`cursor-pointer p-1 transition ${liked ? 'text-accent9' : 'text-gray9 hover:text-gray11'}`}
    >
      <Heart className="h-5 w-5" fill={liked ? 'currentColor' : 'none'} />
    </button>
  )
}

function whenLabel(event: EventFeedEvent) {
  if (event.time_until === 'Today') return 'Today'
  return event.is_past ? `${event.time_until} ago` : `in ${event.time_until}`
}
